import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Volume2, VolumeX } from 'lucide-react';
import { useAskAiVoice } from '../hooks/useAskAiVoice';
import { unlockBrowserSpeech } from '../utils/browserSpeech';
import AaCoreAiName from './AaCoreAiName';
import AskAiRichText from './AskAiRichText';
import { DateChoicePicker, TimeSlotGrid, looksLikeTimeChip } from './AskAiSchedulePickers';
import { AskAiMobileFullscreenButton, AskAiMobileScrim, useAskAiMobileFullscreen } from './AskAiMobileFullscreen';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { getBrowserTimeZone } from '../utils/timezoneUtils';
import { askAiRequestErrorMessage } from '../utils/askAiErrors';
import { clearAskAiSessionId, getOrCreateAskAiSessionId, resetAskAiSessionId } from '../utils/askAiSession';
import {
  AskAiCancelDraft,
  AskAiCancelStatus,
  AskAiReply,
  AskAiRescheduleDraft,
  AskAiRescheduleStatus,
  AskAiSuggestion,
  AskAiTurn,
} from '../types';

function parseHex(color: string): { r: number; g: number; b: number } | null {
  const raw = String(color || '').trim().replace('#', '');
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0] + raw[0], 16),
      g: parseInt(raw[1] + raw[1], 16),
      b: parseInt(raw[2] + raw[2], 16),
    };
  }
  if (raw.length === 6) {
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    };
  }
  return null;
}

function contrastInk(color: string): string {
  const rgb = parseHex(color);
  if (!rgb) return '#ffffff';
  const luma = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luma > 0.62 ? '#111827' : '#ffffff';
}

function indexRecord(value: unknown): Record<number, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<number, unknown> = {};
  Object.keys(value as object).forEach((key) => {
    const index = Number(key);
    const item = (value as Record<string, unknown>)[key];
    if (!Number.isInteger(index) || index < 0 || !item || typeof item !== 'object') return;
    out[index] = item;
  });
  return out;
}

function looksLikeConfirmCancel(query: string): boolean {
  const n = String(query || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  return /^(yes|yes cancel|confirm|confirm cancellation|cancel it|go ahead)$/.test(n);
}

function looksLikeConfirmReschedule(query: string): boolean {
  const n = String(query || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  return /^(yes|yes reschedule|confirm|confirm reschedule|that works|go ahead|reschedule it)$/.test(n);
}

function draftFromCancel(cancel: AskAiCancelStatus): AskAiCancelDraft {
  return {
    wanted: true,
    appointmentId: cancel.appointmentId || '',
    reason: cancel.reason || '',
    skipReason: Boolean(cancel.skipReason),
    scope: cancel.scope,
  };
}

function draftFromReschedule(reschedule: AskAiRescheduleStatus): AskAiRescheduleDraft {
  return {
    wanted: true,
    appointmentId: reschedule.appointmentId || '',
    date: reschedule.date || '',
    time: reschedule.time || '',
  };
}

function rescheduleIdFromUrl(url?: string): string {
  const match = String(url || '').match(/\/dashboard\/customer\/reschedule\/([^/?#]+)/i);
  return match ? match[1] : '';
}

function loadExtras(key: string): {
  suggestions: Record<number, AskAiSuggestion>;
  chips: Record<number, string[]>;
  cancels: Record<number, AskAiCancelStatus>;
  reschedules: Record<number, AskAiRescheduleStatus>;
} {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object') return { suggestions: {}, chips: {}, cancels: {}, reschedules: {} };
    const suggestions = indexRecord((parsed as { suggestions?: unknown }).suggestions) as Record<number, AskAiSuggestion>;
    const chipsRaw = indexRecord((parsed as { chips?: unknown }).chips);
    const chips: Record<number, string[]> = {};
    Object.entries(chipsRaw).forEach(([index, value]) => {
      if (Array.isArray(value)) chips[Number(index)] = value.map(String).filter(Boolean);
    });
    const cancels = indexRecord((parsed as { cancels?: unknown }).cancels) as Record<number, AskAiCancelStatus>;
    const reschedules = indexRecord((parsed as { reschedules?: unknown }).reschedules) as Record<number, AskAiRescheduleStatus>;
    return { suggestions, chips, cancels, reschedules };
  } catch {
    return { suggestions: {}, chips: {}, cancels: {}, reschedules: {} };
  }
}

function CancelReviewCard({
  cancel,
  textColor,
}: {
  cancel: AskAiCancelStatus;
  textColor: string;
}) {
  const muted = `${textColor}99`;
  const line = `${textColor}18`;
  const reason = cancel.skipReason || !cancel.reason ? 'No reason given' : cancel.reason;
  const scope = cancel.needsScope
    ? (cancel.scope === 'REMAINING'
      ? `This and ${cancel.remainingAfterThis || 0} remaining`
      : 'This appointment only')
    : 'This appointment only';
  return (
    <div className="mt-2 overflow-hidden" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
      <div className="px-3 py-2 text-xs font-semibold tracking-wide uppercase" style={{ color: muted, borderBottom: `1px solid ${line}` }}>
        {cancel.cancelled ? 'Appointment cancelled' : 'Cancellation summary'}
      </div>
      <dl className="m-0 px-3 py-2">
        {cancel.summary ? (
          <div className="py-0.5">
            <dt className="text-xs m-0" style={{ color: muted }}>Appointment</dt>
            <dd className="m-0 text-sm font-medium">{cancel.summary}</dd>
          </div>
        ) : null}
        <div className="flex items-baseline justify-between gap-3 py-0.5">
          <dt className="text-xs m-0" style={{ color: muted }}>Reason</dt>
          <dd className="m-0 text-sm font-medium text-right">{reason}</dd>
        </div>
        {cancel.needsScope || cancel.scope ? (
          <div className="flex items-baseline justify-between gap-3 py-0.5">
            <dt className="text-xs m-0" style={{ color: muted }}>Scope</dt>
            <dd className="m-0 text-sm font-medium text-right">{scope}</dd>
          </div>
        ) : null}
      </dl>
      {cancel.policyNote ? (
        <p className="m-0 px-3 py-2 text-xs leading-relaxed" style={{ color: muted, borderTop: `1px solid ${line}` }}>
          {cancel.policyNote}
        </p>
      ) : null}
    </div>
  );
}

function RescheduleReviewCard({
  reschedule,
  textColor,
}: {
  reschedule: AskAiRescheduleStatus;
  textColor: string;
}) {
  const muted = `${textColor}99`;
  const line = `${textColor}18`;
  return (
    <div className="mt-2 overflow-hidden" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
      <div className="px-3 py-2 text-xs font-semibold tracking-wide uppercase" style={{ color: muted, borderBottom: `1px solid ${line}` }}>
        {reschedule.rescheduled ? 'Appointment rescheduled' : 'Reschedule summary'}
      </div>
      <dl className="m-0 px-3 py-2">
        {reschedule.summary ? (
          <div className="py-0.5">
            <dt className="text-xs m-0" style={{ color: muted }}>Appointment</dt>
            <dd className="m-0 text-sm font-medium">{reschedule.summary}</dd>
          </div>
        ) : null}
        {reschedule.currentWhenLabel ? (
          <div className="flex items-baseline justify-between gap-3 py-0.5">
            <dt className="text-xs m-0" style={{ color: muted }}>Current</dt>
            <dd className="m-0 text-sm font-medium text-right">{reschedule.currentWhenLabel}</dd>
          </div>
        ) : null}
        {reschedule.newWhenLabel ? (
          <div className="flex items-baseline justify-between gap-3 py-0.5">
            <dt className="text-xs m-0" style={{ color: muted }}>New time</dt>
            <dd className="m-0 text-sm font-medium text-right">{reschedule.newWhenLabel}</dd>
          </div>
        ) : null}
      </dl>
      {reschedule.policyNote ? (
        <p className="m-0 px-3 py-2 text-xs leading-relaxed" style={{ color: muted, borderTop: `1px solid ${line}` }}>
          {reschedule.policyNote}
        </p>
      ) : null}
    </div>
  );
}

const CUSTOMER_ASK_VISIT_KEY = 'customer-ask:active-dashboard';
const CUSTOMER_ASK_STORAGE_KEY = 'customer-ask';
let customerAskMounts = 0;

function isCustomerDashboardPath(path: string): boolean {
  return String(path || '').split('?')[0].startsWith('/dashboard/customer');
}

function persistCustomerAskOpen(open: boolean): void {
  if (open) sessionStorage.setItem(`${CUSTOMER_ASK_STORAGE_KEY}:open`, '1');
  else sessionStorage.removeItem(`${CUSTOMER_ASK_STORAGE_KEY}:open`);
}

function clearCustomerAskStorage(): void {
  sessionStorage.removeItem(CUSTOMER_ASK_STORAGE_KEY);
  sessionStorage.removeItem(`${CUSTOMER_ASK_STORAGE_KEY}:extras`);
  sessionStorage.removeItem(`${CUSTOMER_ASK_STORAGE_KEY}:cancel`);
  sessionStorage.removeItem(`${CUSTOMER_ASK_STORAGE_KEY}:reschedule`);
  sessionStorage.removeItem(`${CUSTOMER_ASK_STORAGE_KEY}:open`);
  clearAskAiSessionId(CUSTOMER_ASK_STORAGE_KEY);
}

function customerSessionGreeting(firstName?: string): string {
  let hour = new Date().getHours();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hourCycle: 'h23',
      timeZone: getBrowserTimeZone(),
    }).formatToParts(new Date());
    const parsed = parseInt(parts.find((part) => part.type === 'hour')?.value || '', 10);
    if (Number.isFinite(parsed)) hour = parsed;
  } catch {
    // keep local hour
  }
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = String(firstName || '').trim();
  const assist = 'I am here to assist you with your appointments, invoices, and quote requests.';
  return name ? `${hello} ${name}, ${assist}` : `${hello}, ${assist}`;
}

const CustomerAskAiWidget: React.FC = () => {
  const { user } = useAuth();
  const accentColor = '#0B4778';
  const accentInk = contrastInk(accentColor);
  const textColor = '#111827';
  const [isEnabled, setIsEnabled] = useState(false);
  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(`${CUSTOMER_ASK_STORAGE_KEY}:open`) === '1';
    } catch {
      return false;
    }
  });
  const [pending, setPending] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<AskAiTurn[]>([]);
  const [cancelDraft, setCancelDraft] = useState<AskAiCancelDraft>({});
  const [rescheduleDraft, setRescheduleDraft] = useState<AskAiRescheduleDraft>({});
  const [suggestionsByIndex, setSuggestionsByIndex] = useState<Record<number, AskAiSuggestion>>({});
  const [chipsByIndex, setChipsByIndex] = useState<Record<number, string[]>>({});
  const [cancelByIndex, setCancelByIndex] = useState<Record<number, AskAiCancelStatus>>({});
  const [rescheduleByIndex, setRescheduleByIndex] = useState<Record<number, AskAiRescheduleStatus>>({});
  const dockRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);
  const askRef = useRef<(query: string) => Promise<void>>(async () => undefined);
  const listenBaseRef = useRef('');
  const voice = useAskAiVoice({
    active: open,
    onInterimTranscript: (text) => {
      setInput([listenBaseRef.current, text].filter(Boolean).join(' ').trim());
    },
    onFinalTranscript: (text) => {
      const next = [listenBaseRef.current, text].filter(Boolean).join(' ').trim();
      listenBaseRef.current = '';
      if (next) {
        setInput('');
        void askRef.current(next);
      }
    },
  });
  const { setSpeechError, stopListening, stopSpeaking, speak } = voice;
  const speakRef = useRef(speak);
  speakRef.current = speak;
  const suggestionsRef = useRef<Record<number, AskAiSuggestion>>({});
  const chipsRef = useRef<Record<number, string[]>>({});
  const cancelsRef = useRef<Record<number, AskAiCancelStatus>>({});
  const reschedulesRef = useRef<Record<number, AskAiRescheduleStatus>>({});
  const cancelDraftRef = useRef<AskAiCancelDraft>({});
  const rescheduleDraftRef = useRef<AskAiRescheduleDraft>({});

  const storageKey = CUSTOMER_ASK_STORAGE_KEY;
  const extrasKey = `${storageKey}:extras`;
  const cancelKey = `${storageKey}:cancel`;
  const rescheduleKey = `${storageKey}:reschedule`;
  const actionBusy = pending || cancelBusy || rescheduleBusy;
  const { mobileFullscreen, mobileCompact, toggleFullscreen } = useAskAiMobileFullscreen(open);

  useEffect(() => {
    let cancelled = false;
    apiService.getCustomerAskAiStatus()
      .then((data) => {
        if (!cancelled) setIsEnabled(Boolean(data.enabled));
      })
      .catch(() => {
        if (!cancelled) setIsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persistHistory = useCallback((next: AskAiTurn[]) => {
    sessionStorage.setItem(storageKey, JSON.stringify(next.map((turn) => ({ role: turn.role, content: turn.content }))));
  }, [storageKey]);

  const persistCancel = useCallback((next: AskAiCancelDraft) => {
    cancelDraftRef.current = next;
    if (!next.wanted) {
      sessionStorage.removeItem(cancelKey);
      return;
    }
    sessionStorage.setItem(cancelKey, JSON.stringify(next));
  }, [cancelKey]);

  const persistReschedule = useCallback((next: AskAiRescheduleDraft) => {
    rescheduleDraftRef.current = next;
    if (!next.wanted) {
      sessionStorage.removeItem(rescheduleKey);
      return;
    }
    sessionStorage.setItem(rescheduleKey, JSON.stringify(next));
  }, [rescheduleKey]);

  const persistExtras = useCallback((
    suggestions: Record<number, AskAiSuggestion>,
    chips: Record<number, string[]>,
    cancels: Record<number, AskAiCancelStatus>,
    reschedules: Record<number, AskAiRescheduleStatus>
  ) => {
    suggestionsRef.current = suggestions;
    chipsRef.current = chips;
    cancelsRef.current = cancels;
    reschedulesRef.current = reschedules;
    if (
      Object.keys(suggestions).length === 0
      && Object.keys(chips).length === 0
      && Object.keys(cancels).length === 0
      && Object.keys(reschedules).length === 0
    ) {
      sessionStorage.removeItem(extrasKey);
      return;
    }
    sessionStorage.setItem(extrasKey, JSON.stringify({ suggestions, chips, cancels, reschedules }));
  }, [extrasKey]);

  const applyCancelReply = useCallback((cancel?: AskAiCancelStatus) => {
    if (!cancel) return;
    if (!cancel.wanted || cancel.cancelled) {
      setCancelDraft({});
      persistCancel({});
      return;
    }
    const next = draftFromCancel(cancel);
    setCancelDraft(next);
    persistCancel(next);
    setRescheduleDraft({});
    persistReschedule({});
  }, [persistCancel, persistReschedule]);

  const applyRescheduleReply = useCallback((reschedule?: AskAiRescheduleStatus) => {
    if (!reschedule) return;
    if (!reschedule.wanted || reschedule.rescheduled) {
      setRescheduleDraft({});
      persistReschedule({});
      return;
    }
    const next = draftFromReschedule(reschedule);
    setRescheduleDraft(next);
    persistReschedule(next);
    setCancelDraft({});
    persistCancel({});
  }, [persistCancel, persistReschedule]);

  const startFreshSession = useCallback(() => {
    resetAskAiSessionId(storageKey);
    const greeting: AskAiTurn[] = [{ role: 'assistant', content: customerSessionGreeting(user?.firstName) }];
    setHistory(greeting);
    setSuggestionsByIndex({});
    setChipsByIndex({});
    setCancelByIndex({});
    setRescheduleByIndex({});
    setCancelDraft({});
    setRescheduleDraft({});
    persistHistory(greeting);
    persistExtras({}, {}, {}, {});
    persistCancel({});
    persistReschedule({});
    persistCustomerAskOpen(false);
    setStatus('');
    setStatusError(false);
  }, [user?.firstName, persistHistory, persistExtras, persistCancel, persistReschedule, storageKey]);
  const startFreshSessionRef = useRef(startFreshSession);
  startFreshSessionRef.current = startFreshSession;

  useEffect(() => {
    const storedTurns = sessionStorage.getItem(storageKey);
    const hasStoredSession = Boolean(storedTurns && storedTurns !== '[]');
    const continuing = sessionStorage.getItem(CUSTOMER_ASK_VISIT_KEY) === '1'
      || (hasStoredSession && isCustomerDashboardPath(window.location.pathname));
    sessionStorage.setItem(CUSTOMER_ASK_VISIT_KEY, '1');
    customerAskMounts += 1;

    if (continuing) {
      try {
        const parsed = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        if (Array.isArray(parsed)) {
          setHistory(
            parsed
              .filter((turn: AskAiTurn) => turn && (turn.role === 'user' || turn.role === 'assistant') && typeof turn.content === 'string')
              .map((turn: AskAiTurn) => ({ role: turn.role, content: turn.content.trim() }))
              .filter((turn: AskAiTurn) => turn.content)
          );
        }
      } catch {
        setHistory([]);
      }
      try {
        const parsed = JSON.parse(sessionStorage.getItem(cancelKey) || 'null');
        if (parsed && typeof parsed === 'object') {
          const next: AskAiCancelDraft = {
            wanted: Boolean(parsed.wanted),
            appointmentId: String(parsed.appointmentId || ''),
            reason: String(parsed.reason || ''),
            skipReason: Boolean(parsed.skipReason),
            scope: parsed.scope === 'REMAINING' || parsed.scope === 'THIS' ? parsed.scope : undefined,
          };
          cancelDraftRef.current = next;
          setCancelDraft(next);
        }
      } catch {
        cancelDraftRef.current = {};
        setCancelDraft({});
      }
      try {
        const parsed = JSON.parse(sessionStorage.getItem(rescheduleKey) || 'null');
        if (parsed && typeof parsed === 'object') {
          const next: AskAiRescheduleDraft = {
            wanted: Boolean(parsed.wanted),
            appointmentId: String(parsed.appointmentId || ''),
            date: String(parsed.date || ''),
            time: String(parsed.time || ''),
          };
          rescheduleDraftRef.current = next;
          setRescheduleDraft(next);
        }
      } catch {
        rescheduleDraftRef.current = {};
        setRescheduleDraft({});
      }
      const extras = loadExtras(extrasKey);
      suggestionsRef.current = extras.suggestions;
      chipsRef.current = extras.chips;
      cancelsRef.current = extras.cancels;
      reschedulesRef.current = extras.reschedules;
      setSuggestionsByIndex(extras.suggestions);
      setChipsByIndex(extras.chips);
      setCancelByIndex(extras.cancels);
      setRescheduleByIndex(extras.reschedules);
      if (sessionStorage.getItem(`${storageKey}:open`) === '1') setOpen(true);
    } else {
      startFreshSessionRef.current();
    }

    return () => {
      customerAskMounts = Math.max(0, customerAskMounts - 1);
      window.setTimeout(() => {
        if (customerAskMounts > 0) return;
        if (isCustomerDashboardPath(window.location.pathname)) return;
        clearCustomerAskStorage();
        sessionStorage.removeItem(CUSTOMER_ASK_VISIT_KEY);
      }, 100);
    };
  }, [storageKey, extrasKey, cancelKey, rescheduleKey]);

  useEffect(() => {
    if (!user?.firstName) return;
    setHistory((current) => {
      if (current.length !== 1 || current[0].role !== 'assistant') return current;
      if (current[0].content.includes(`${user.firstName},`)) return current;
      if (!/^Good (morning|afternoon|evening), /.test(current[0].content)) return current;
      const next = [{ role: 'assistant' as const, content: customerSessionGreeting(user.firstName) }];
      persistHistory(next);
      return next;
    });
  }, [user?.firstName, persistHistory]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [history, open, pending, cancelBusy, rescheduleBusy]);

  useEffect(() => {
    if (!open || pending || cancelBusy || rescheduleBusy || voice.listening) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, pending, cancelBusy, rescheduleBusy, history.length, voice.listening]);

  const openPanel = () => {
    lastFocusRef.current = document.activeElement as HTMLElement;
    persistCustomerAskOpen(true);
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const resetChat = () => {
    stopSpeaking();
    startFreshSession();
    persistCustomerAskOpen(true);
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const appendAssistant = (answer: string, data: AskAiReply) => {
    if (answer) void speakRef.current(answer);
    setHistory((current) => {
      const next = [...current, { role: 'assistant' as const, content: answer }];
      persistHistory(next);
      const index = next.length - 1;
      const suggestion = (data.suggestions || [])[0];
      const suggestions = suggestion ? { ...suggestionsRef.current, [index]: suggestion } : suggestionsRef.current;
      const chips = (data.reschedule?.chips && data.reschedule.chips.length > 0)
        ? { ...chipsRef.current, [index]: data.reschedule.chips }
        : (data.cancel?.chips && data.cancel.chips.length > 0)
          ? { ...chipsRef.current, [index]: data.cancel.chips }
          : (data.chips && data.chips.length > 0)
            ? { ...chipsRef.current, [index]: data.chips }
            : chipsRef.current;
      const cancels = data.cancel?.wanted
        ? { ...cancelsRef.current, [index]: data.cancel }
        : cancelsRef.current;
      const reschedules = data.reschedule?.wanted
        ? { ...reschedulesRef.current, [index]: data.reschedule }
        : reschedulesRef.current;
      setSuggestionsByIndex(suggestions);
      setChipsByIndex(chips);
      setCancelByIndex(cancels);
      setRescheduleByIndex(reschedules);
      persistExtras(suggestions, chips, cancels, reschedules);
      return next;
    });
  };

  const confirmCancel = async (cancel: AskAiCancelStatus, messageIndex: number) => {
    if (cancelBusy || !cancel.ready || !cancel.appointmentId) return;
    setCancelBusy(true);
    setStatus('');
    setStatusError(false);
    try {
      await apiService.cancelAppointment(cancel.appointmentId, {
        noShow: false,
        cancellationNotes: cancel.skipReason ? '' : (cancel.reason || ''),
        cancelScope: cancel.needsScope ? (cancel.scope || 'THIS') : undefined,
      });
      const reply: AskAiReply = {
        answer: 'Your appointment has been cancelled. You can view your bookings or book again anytime.',
        suggestions: [{
          short_name: 'appointments',
          name: 'Appointments',
          primary: { label: 'See appointments', url: '/dashboard/customer/appointments' },
          actions: [
            ...(cancel.bookingUrl ? [{ label: 'Book again', url: cancel.bookingUrl }] : []),
            ...(cancel.appointmentUrl ? [{ label: 'View appointment', url: cancel.appointmentUrl }] : []),
          ],
        }],
        cancel: {
          ...cancel,
          ready: false,
          cancelled: true,
        },
      };
      applyCancelReply(reply.cancel);
      setCancelByIndex((current) => {
        const next = { ...current, [messageIndex]: { ...cancel, ready: false } };
        cancelsRef.current = next;
        return next;
      });
      appendAssistant(reply.answer, reply);
    } catch (error: any) {
      setStatus(error?.response?.data?.message || 'I could not cancel that appointment. Please try again.');
      setStatusError(true);
    } finally {
      setCancelBusy(false);
    }
  };

  const confirmReschedule = async (reschedule: AskAiRescheduleStatus, messageIndex: number) => {
    if (rescheduleBusy || !reschedule.ready || !reschedule.appointmentId || !reschedule.scheduledAt) return;
    setRescheduleBusy(true);
    setStatus('');
    setStatusError(false);
    try {
      await apiService.rescheduleAppointment(reschedule.appointmentId, {
        scheduledAt: reschedule.scheduledAt,
      });
      const reply: AskAiReply = {
        answer: `Your appointment is now ${reschedule.newWhenLabel || 'at the new time'}. You can view the updated booking anytime.`,
        suggestions: [{
          short_name: 'appointments',
          name: 'Appointments',
          primary: { label: 'See appointments', url: '/dashboard/customer/appointments' },
          actions: [
            ...(reschedule.appointmentUrl ? [{ label: 'View appointment', url: reschedule.appointmentUrl }] : []),
          ],
        }],
        reschedule: {
          ...reschedule,
          ready: false,
          rescheduled: true,
        },
      };
      applyRescheduleReply(reply.reschedule);
      setRescheduleByIndex((current) => {
        const next = { ...current, [messageIndex]: { ...reschedule, ready: false } };
        reschedulesRef.current = next;
        return next;
      });
      appendAssistant(reply.answer, reply);
    } catch (error: any) {
      setStatus(error?.response?.data?.message || 'I could not reschedule that appointment. Please try again.');
      setStatusError(true);
    } finally {
      setRescheduleBusy(false);
    }
  };

  const ask = async (query: string, override?: { cancel?: AskAiCancelDraft; reschedule?: AskAiRescheduleDraft }) => {
    if (actionBusy) return;
    stopListening();
    stopSpeaking();
    setSpeechError('');
    unlockBrowserSpeech();
    let latestCancelReadyIndex: number | undefined;
    let latestRescheduleReadyIndex: number | undefined;
    for (let index = history.length - 1; index >= 0; index -= 1) {
      if (history[index].role !== 'assistant') continue;
      if (latestCancelReadyIndex == null && cancelByIndex[index]?.ready && !cancelByIndex[index]?.cancelled) {
        latestCancelReadyIndex = index;
      }
      if (latestRescheduleReadyIndex == null && rescheduleByIndex[index]?.ready && !rescheduleByIndex[index]?.rescheduled) {
        latestRescheduleReadyIndex = index;
      }
      if (latestCancelReadyIndex != null && latestRescheduleReadyIndex != null) break;
    }
    const latestCancelReady = latestCancelReadyIndex != null ? cancelByIndex[latestCancelReadyIndex] : undefined;
    const latestRescheduleReady = latestRescheduleReadyIndex != null ? rescheduleByIndex[latestRescheduleReadyIndex] : undefined;
    if (!override && latestRescheduleReady && looksLikeConfirmReschedule(query)) {
      setHistory((current) => {
        const next = [...current, { role: 'user' as const, content: query }];
        persistHistory(next);
        return next;
      });
      await confirmReschedule(latestRescheduleReady, latestRescheduleReadyIndex as number);
      return;
    }
    if (!override && latestCancelReady && looksLikeConfirmCancel(query)) {
      setHistory((current) => {
        const next = [...current, { role: 'user' as const, content: query }];
        persistHistory(next);
        return next;
      });
      await confirmCancel(latestCancelReady, latestCancelReadyIndex as number);
      return;
    }

    const prior = history.map((turn) => ({ role: turn.role, content: turn.content }));
    const cancelPayload = override?.cancel
      ? { ...cancelDraftRef.current, ...override.cancel, wanted: true }
      : cancelDraftRef.current;
    const reschedulePayload = override?.reschedule
      ? { ...rescheduleDraftRef.current, ...override.reschedule, wanted: true }
      : rescheduleDraftRef.current;
    setPending(true);
    setStatus('');
    setStatusError(false);
    setHistory((current) => [...current, { role: 'user', content: query }]);
    try {
      const data: AskAiReply = await apiService.askCustomer({
        query,
        history: prior,
        timeZone: getBrowserTimeZone(),
        cancel: override?.reschedule ? undefined : (cancelPayload.wanted ? cancelPayload : undefined),
        reschedule: override?.cancel ? undefined : (reschedulePayload.wanted ? reschedulePayload : undefined),
        sessionId: getOrCreateAskAiSessionId(storageKey),
      });
      if (data.reschedule?.wanted) applyRescheduleReply(data.reschedule);
      else if (data.cancel?.wanted) applyCancelReply(data.cancel);
      else if (data.reschedule) applyRescheduleReply(data.reschedule);
      else if (data.cancel) applyCancelReply(data.cancel);
      appendAssistant(data.answer || 'Here is what I can share.', data);
    } catch (error: any) {
      setHistory((current) => current.slice(0, -1));
      setStatus(askAiRequestErrorMessage(error));
      setStatusError(true);
    } finally {
      setPending(false);
    }
  };
  askRef.current = (query) => ask(query);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const query = input.trim();
    if (!query) {
      setStatus('Type a question about your bookings.');
      setStatusError(true);
      return;
    }
    setInput('');
    void ask(query);
  };

  if (!isEnabled) return null;

  const hasChat = history.length > 0;
  const hasUserChat = history.some((turn) => turn.role === 'user');
  const hints = ['When is my next appointment?', 'Reschedule an appointment', 'Cancel an appointment'];

  const closePanel = () => {
    stopListening();
    stopSpeaking();
    persistCustomerAskOpen(false);
    setOpen(false);
  };

  return (
    <>
    <AskAiMobileScrim show={mobileCompact} onClose={closePanel} />
    <div
      ref={dockRef}
      className={`fixed flex flex-col-reverse items-end pointer-events-none ${
        mobileFullscreen
          ? 'inset-0 z-[1100] gap-0'
          : `${mobileCompact ? 'z-[1100]' : 'z-50'} gap-3 right-[max(1.15rem,env(safe-area-inset-right))] bottom-24 lg:bottom-[max(1.15rem,env(safe-area-inset-bottom))]`
      }`}
      style={{ width: mobileFullscreen ? '100%' : 'min(23rem, calc(100vw - 2.3rem))' }}
    >
      <button
        type="button"
        className={`${open ? 'max-md:hidden' : ''} pointer-events-auto inline-flex items-center self-end gap-2 rounded-full px-4 py-3 shadow-lg font-semibold text-sm`}
        style={{ background: accentColor, color: accentInk }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="customer-ask-dialog"
        onClick={() => (open ? closePanel() : openPanel())}
      >
        <AaCoreAiName
          ask
          color={accentInk}
          aiColor={accentInk === '#ffffff' ? '#FDE68A' : accentColor}
          iconClassName="w-5 h-5"
          className="text-sm"
        />
      </button>

      {open && (
        <div className={`pointer-events-auto w-full ${mobileFullscreen ? 'h-full' : ''}`}>
          <div
            id="customer-ask-dialog"
            role="dialog"
            aria-modal={mobileCompact || mobileFullscreen}
            aria-labelledby="customer-ask-dialog-title"
            className={`flex flex-col w-full overflow-hidden bg-white border shadow-2xl ${
              mobileFullscreen
                ? ''
                : 'h-[min(36rem,calc(100dvh-11rem))] md:h-[min(36rem,calc(100dvh-15rem))] lg:h-[min(36rem,calc(100dvh-10rem))]'
            }`}
            style={{
              height: mobileFullscreen ? '100%' : undefined,
              color: textColor,
              borderColor: `${textColor}22`,
              borderRadius: mobileFullscreen ? 0 : 16,
            }}
          >
            <header className={`flex flex-col gap-1 px-4 pb-3 flex-shrink-0 ${mobileFullscreen ? 'pt-[max(1rem,env(safe-area-inset-top))]' : 'pt-4'}`}>
              <div className="flex justify-between items-start gap-3">
                <h2 id="customer-ask-dialog-title" className="text-lg font-semibold m-0 tracking-tight min-w-0">
                  <AaCoreAiName ask color={textColor} aiColor={accentColor} iconClassName="w-5 h-5" className="text-lg" />
                </h2>
                <div className="flex items-center gap-2 shrink-0">
                {voice.canSpeak ? (
                  <button
                    type="button"
                    className="w-8 h-8 border bg-transparent inline-flex items-center justify-center cursor-pointer"
                    style={{ borderColor: `${textColor}22`, color: voice.speaking ? accentColor : textColor, borderRadius: 8 }}
                    aria-label={voice.voiceReplies ? 'Mute spoken replies' : 'Unmute spoken replies'}
                    aria-pressed={voice.voiceReplies}
                    title={voice.voiceReplies ? 'Mute spoken replies' : 'Speak replies aloud'}
                    onClick={voice.toggleVoiceReplies}
                  >
                    {voice.voiceReplies ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                ) : null}
                {hasChat && (
                  <button
                    type="button"
                    className="h-8 px-3 text-xs inline-flex items-center justify-center whitespace-nowrap shrink-0 border bg-transparent cursor-pointer box-border"
                    style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                    onClick={resetChat}
                  >
                    New chat
                  </button>
                )}
                <AskAiMobileFullscreenButton
                  fullscreen={mobileFullscreen}
                  onToggle={toggleFullscreen}
                  textColor={textColor}
                />
                <button
                  type="button"
                  className="w-8 h-8 border bg-transparent text-xl leading-none cursor-pointer"
                  style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                  aria-label="Close Ask AACore AI"
                  onClick={closePanel}
                >
                  ×
                </button>
                </div>
              </div>
              <p className="w-full mt-0 mb-0 text-sm" style={{ color: `${textColor}99` }}>
                Bookings, cancellations, reschedules, and more
              </p>
            </header>

            <div ref={threadRef} className="flex-1 min-h-0 overflow-auto flex flex-col gap-2 px-4 pb-2" aria-live="polite">
              {history.map((turn, index) => {
                const suggestion = suggestionsByIndex[index];
                const chips = chipsByIndex[index] || [];
                const cancel = cancelByIndex[index];
                const reschedule = rescheduleByIndex[index];
                const isUser = turn.role === 'user';
                const showCancelPicks = Boolean(cancel?.picks && cancel.picks.length > 0 && !cancel.ready && !cancel.cancelled);
                const showReschedulePicks = Boolean(reschedule?.picks && reschedule.picks.length > 0 && !reschedule.ready && !reschedule.rescheduled);
                const showPicks = showCancelPicks || showReschedulePicks;
                const showCancelChips = Boolean(!showCancelPicks && cancel?.chips && cancel.chips.length > 0 && !cancel.ready && !cancel.cancelled);
                const showDatePicker = Boolean(
                  reschedule?.wanted
                  && !reschedule.ready
                  && !reschedule.rescheduled
                  && !reschedule.cannotSelfReschedule
                  && !showReschedulePicks
                  && (reschedule.dateOptions?.length || reschedule.showCalendar)
                );
                const showTimeGrid = Boolean(
                  reschedule?.wanted
                  && !reschedule.ready
                  && !reschedule.rescheduled
                  && !reschedule.cannotSelfReschedule
                  && !showReschedulePicks
                  && ((reschedule.availableTimes && reschedule.availableTimes.length > 0) || (reschedule.chips || []).some(looksLikeTimeChip))
                );
                const wide = Boolean(
                  cancel?.ready || cancel?.cancelled || reschedule?.ready || reschedule?.rescheduled || showPicks || showDatePicker || showTimeGrid
                );
                const startRescheduleFromUrl = (url?: string) => {
                  const id = rescheduleIdFromUrl(url);
                  if (!id || reschedule?.cannotSelfReschedule) return false;
                  void ask('Reschedule this appointment', { reschedule: { wanted: true, appointmentId: id } });
                  return true;
                };
                return (
                  <div
                    key={`${turn.role}-${index}`}
                    className={`max-w-[96%] px-3 py-2 text-sm leading-relaxed ${isUser ? 'self-end' : 'self-start border'}`}
                    style={
                      isUser
                        ? { background: accentColor, color: accentInk, borderRadius: 18 }
                        : { borderColor: `${textColor}22`, color: textColor, borderRadius: 18, minWidth: wide ? '16.5rem' : undefined }
                    }
                  >
                    {!isUser && (
                      <AaCoreAiName color={`${textColor}99`} aiColor={accentColor} className="mb-1.5" />
                    )}
                    {isUser ? (
                      <p className="m-0 whitespace-pre-wrap">{turn.content}</p>
                    ) : (
                      <AskAiRichText text={turn.content} accentColor={accentColor} />
                    )}
                    {!isUser && (cancel?.ready || cancel?.cancelled) && (
                      <CancelReviewCard cancel={cancel} textColor={textColor} />
                    )}
                    {!isUser && (reschedule?.ready || reschedule?.rescheduled) && (
                      <RescheduleReviewCard reschedule={reschedule} textColor={textColor} />
                    )}
                    {!isUser && cancel?.ready && !cancel.cancelled && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Change appointment', { cancel: { wanted: true, appointmentId: '', reason: cancel.reason, skipReason: cancel.skipReason } })}
                        >
                          Change appointment
                        </button>
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Change reason', { cancel: { wanted: true, appointmentId: cancel.appointmentId, reason: '', skipReason: false, scope: cancel.scope } })}
                        >
                          Change reason
                        </button>
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Keep appointment')}
                        >
                          Keep appointment
                        </button>
                      </div>
                    )}
                    {!isUser && reschedule?.ready && !reschedule.rescheduled && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Change appointment', { reschedule: { wanted: true, appointmentId: '', date: '', time: '', resetDate: true, resetTime: true } })}
                        >
                          Change appointment
                        </button>
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Change date', { reschedule: { wanted: true, appointmentId: reschedule.appointmentId, resetDate: true, resetTime: true } })}
                        >
                          Change date
                        </button>
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Change time', { reschedule: { wanted: true, appointmentId: reschedule.appointmentId, date: reschedule.date, resetTime: true } })}
                        >
                          Change time
                        </button>
                        <button
                          type="button"
                          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                          style={{ borderColor: `${textColor}22`, color: textColor }}
                          disabled={actionBusy || index < history.length - 1}
                          onClick={() => void ask('Keep appointment')}
                        >
                          Keep current time
                        </button>
                      </div>
                    )}
                    {!isUser && suggestion && !cancel?.ready && !cancel?.cancelled && !reschedule?.ready && !reschedule?.rescheduled && (suggestion.primary?.url || (suggestion.actions || []).some((action) => action.url)) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {suggestion.primary?.url && (
                          rescheduleIdFromUrl(suggestion.primary.url) && !reschedule?.cannotSelfReschedule ? (
                            <button
                              type="button"
                              className="text-xs font-semibold border-0 px-2 py-1 cursor-pointer disabled:opacity-60"
                              style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                              disabled={actionBusy || index < history.length - 1}
                              onClick={() => startRescheduleFromUrl(suggestion.primary?.url)}
                            >
                              {suggestion.primary.label}
                            </button>
                          ) : (
                            <a
                              href={suggestion.primary.url}
                              className="text-xs font-semibold no-underline px-2 py-1"
                              style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                            >
                              {suggestion.primary.label}
                            </a>
                          )
                        )}
                        {(suggestion.actions || []).filter((action) => action.url).map((action) => (
                          rescheduleIdFromUrl(action.url) && !reschedule?.cannotSelfReschedule ? (
                            <button
                              key={action.url}
                              type="button"
                              className="text-xs font-semibold bg-transparent border px-2 py-1 cursor-pointer disabled:opacity-60"
                              style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                              disabled={actionBusy || index < history.length - 1}
                              onClick={() => startRescheduleFromUrl(action.url)}
                            >
                              {action.label}
                            </button>
                          ) : (
                            <a
                              key={action.url}
                              href={action.url}
                              className="text-xs font-semibold no-underline px-2 py-1 border"
                              style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                            >
                              {action.label}
                            </a>
                          )
                        ))}
                      </div>
                    )}
                    {!isUser && showCancelPicks && (
                      <div className="flex flex-col gap-2 mt-2">
                        {cancel!.picks!.map((pick) => (
                          <button
                            key={pick.appointmentId}
                            type="button"
                            className="text-xs text-left bg-transparent border px-2 py-1.5 cursor-pointer disabled:opacity-60"
                            style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                            disabled={actionBusy || index < history.length - 1}
                            onClick={() => void ask(pick.label, { cancel: { wanted: true, appointmentId: pick.appointmentId } })}
                          >
                            {pick.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {!isUser && showReschedulePicks && (
                      <div className="flex flex-col gap-2 mt-2">
                        {reschedule!.picks!.map((pick) => (
                          <button
                            key={pick.appointmentId}
                            type="button"
                            className="text-xs text-left bg-transparent border px-2 py-1.5 cursor-pointer disabled:opacity-60"
                            style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                            disabled={actionBusy || index < history.length - 1}
                            onClick={() => void ask(pick.label, { reschedule: { wanted: true, appointmentId: pick.appointmentId } })}
                          >
                            {pick.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {!isUser && showDatePicker && (
                      <DateChoicePicker
                        options={reschedule!.dateOptions || []}
                        showCalendar={reschedule!.showCalendar}
                        accentColor={accentColor}
                        accentInk={accentInk}
                        textColor={textColor}
                        disabled={actionBusy || index < history.length - 1}
                        onPick={(ymd, label) => {
                          const spoken = label === 'Today' || label === 'Tomorrow' ? label : ymd;
                          void ask(spoken, { reschedule: { wanted: true, appointmentId: reschedule?.appointmentId, date: ymd, resetTime: true } });
                        }}
                      />
                    )}
                    {!isUser && showTimeGrid && (
                      <TimeSlotGrid
                        times={
                          reschedule!.availableTimes && reschedule!.availableTimes.length > 0
                            ? reschedule!.availableTimes
                            : (reschedule!.chips || []).filter(looksLikeTimeChip)
                        }
                        textColor={textColor}
                        disabled={actionBusy || index < history.length - 1}
                        onPick={(time, label) => {
                          const hhmm = /^([01]?\d|2[0-3]):[0-5]\d$/.test(time) ? time : undefined;
                          void ask(label, {
                            reschedule: {
                              wanted: true,
                              appointmentId: reschedule?.appointmentId,
                              date: reschedule?.date,
                              time: hhmm,
                              resetTime: !hhmm,
                            },
                          });
                        }}
                      />
                    )}
                    {!isUser && !showReschedulePicks && !showTimeGrid && reschedule?.chips && reschedule.chips.length > 0 && !reschedule.ready && !reschedule.rescheduled && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {reschedule.chips.filter((chip) => !looksLikeTimeChip(chip)).map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                            style={{ borderColor: `${textColor}22`, color: textColor }}
                            disabled={actionBusy || index < history.length - 1}
                            onClick={() => void ask(chip, { reschedule: { wanted: true, appointmentId: chip === 'Change appointment' ? '' : reschedule.appointmentId } })}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    )}
                    {!isUser && showCancelChips && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {cancel!.chips!.map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                            style={{ borderColor: `${textColor}22`, color: textColor }}
                            disabled={actionBusy || index < history.length - 1}
                            onClick={() => void ask(chip, { cancel: { wanted: true, appointmentId: cancel?.appointmentId } })}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    )}
                    {!isUser && !cancel?.wanted && !reschedule?.wanted && chips.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {chips.map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                            style={{ borderColor: `${textColor}22`, color: textColor }}
                            disabled={actionBusy || index < history.length - 1}
                            onClick={() => void ask(chip)}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    )}
                    {!isUser && cancel?.ready && (
                      <div className="mt-3">
                        <button
                          type="button"
                          className="text-sm font-semibold px-3 py-2 w-full cursor-pointer disabled:opacity-60"
                          style={{ background: accentColor, color: accentInk, borderRadius: 10, border: 0 }}
                          disabled={cancelBusy || index < history.length - 1}
                          onClick={() => void confirmCancel(cancel, index)}
                        >
                          {cancelBusy ? 'Cancelling…' : 'Confirm cancellation'}
                        </button>
                      </div>
                    )}
                    {!isUser && reschedule?.ready && !reschedule.rescheduled && (
                      <div className="mt-3">
                        <button
                          type="button"
                          className="text-sm font-semibold px-3 py-2 w-full cursor-pointer disabled:opacity-60"
                          style={{ background: accentColor, color: accentInk, borderRadius: 10, border: 0 }}
                          disabled={rescheduleBusy || index < history.length - 1}
                          onClick={() => void confirmReschedule(reschedule, index)}
                        >
                          {rescheduleBusy ? 'Rescheduling…' : 'Confirm reschedule'}
                        </button>
                      </div>
                    )}
                    {!isUser && cancel?.cancelled && (cancel.appointmentUrl || cancel.bookingUrl) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <a
                          href="/dashboard/customer/appointments"
                          className="text-xs font-semibold no-underline px-2 py-1"
                          style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                        >
                          See appointments
                        </a>
                        {cancel.bookingUrl && (
                          <a
                            href={cancel.bookingUrl}
                            className="text-xs font-semibold no-underline px-2 py-1 border"
                            style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                          >
                            Book again
                          </a>
                        )}
                      </div>
                    )}
                    {!isUser && reschedule?.rescheduled && reschedule.appointmentUrl && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <a
                          href="/dashboard/customer/appointments"
                          className="text-xs font-semibold no-underline px-2 py-1"
                          style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                        >
                          See appointments
                        </a>
                        <a
                          href={reschedule.appointmentUrl}
                          className="text-xs font-semibold no-underline px-2 py-1 border"
                          style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                        >
                          View appointment
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
              {actionBusy && (
                <div
                  className="self-start max-w-[92%] px-3 py-2 text-sm italic border border-dashed"
                  style={{ borderColor: `${textColor}22`, color: `${textColor}99`, borderRadius: 18 }}
                >
                  {rescheduleBusy ? 'Rescheduling…' : cancelBusy ? 'Cancelling…' : 'Thinking…'}
                </div>
              )}
            </div>

            {!hasUserChat && (
              <p className="text-sm px-4 pb-2 m-0 flex-shrink-0" style={{ color: `${textColor}99` }}>
                Try:
                {hints.map((hint) => (
                  <button
                    key={hint}
                    type="button"
                    className="text-xs bg-transparent border rounded-full px-2 py-0.5 mr-1 mt-1 cursor-pointer"
                    style={{ borderColor: `${textColor}22`, color: textColor }}
                    onClick={() => void ask(hint)}
                  >
                    {hint.replace(/\?$/, '')}
                  </button>
                ))}
              </p>
            )}

            <form className={`flex items-end gap-2 px-4 pt-3 border-t flex-shrink-0 ${mobileFullscreen ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]' : 'pb-3'}`} style={{ borderColor: `${textColor}22` }} onSubmit={submit}>
              <label className="block min-w-0 flex-1">
                <span className="sr-only">Ask AACore AI</span>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(event) => {
                    listenBaseRef.current = event.target.value;
                    setInput(event.target.value);
                  }}
                  maxLength={800}
                  autoComplete="off"
                  placeholder={voice.listening ? 'Listening…' : hasUserChat ? 'Ask a follow-up…' : 'Ask about your appointments…'}
                  className="w-full px-3 py-2 border text-sm"
                  style={{
                    color: textColor,
                    borderColor: voice.listening ? '#e3a3a3' : `${textColor}22`,
                    borderRadius: 10,
                    background: '#fff',
                    boxShadow: voice.listening ? '0 0 0 2px #fde8e8' : undefined,
                  }}
                  disabled={actionBusy}
                />
              </label>
              {voice.canListen ? (
                <button
                  type="button"
                  className="w-10 h-10 shrink-0 inline-flex items-center justify-center border-0 cursor-pointer disabled:opacity-60"
                  style={{
                    background: voice.listening ? '#fde8e8' : `${accentColor}14`,
                    color: voice.listening ? '#b42318' : accentColor,
                    borderRadius: 10,
                  }}
                  aria-label={voice.listening ? 'Stop voice input' : 'Speak your message'}
                  aria-pressed={voice.listening}
                  title={voice.listening ? 'Stop listening' : 'Talk to Ask AACore AI'}
                  disabled={actionBusy}
                  onClick={() => {
                    if (voice.listening) {
                      voice.stopListening();
                      return;
                    }
                    listenBaseRef.current = input;
                    voice.startListening();
                  }}
                >
                  <Mic className="w-5 h-5" />
                </button>
              ) : null}
              <button
                type="submit"
                disabled={actionBusy}
                className="font-semibold px-4 py-2 text-sm disabled:opacity-60 shrink-0"
                style={{ background: accentColor, color: accentInk, borderRadius: 10 }}
              >
                Send
              </button>
            </form>
            {(status || voice.speechError) && (
              <p className={`px-4 pb-3 m-0 text-sm flex-shrink-0 ${statusError ? 'text-red-700' : ''}`} style={statusError ? undefined : { color: `${textColor}99` }}>
                {status || voice.speechError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CustomerAskAiWidget;
